import React, { createContext, useContext, useEffect, useState } from 'react';
import { apiUrl } from '../api/apiUrl';
import useLocalStorage from '../hooks/useLocalStorage';
import { LangSetting } from '../types/core/language';
import { EntityType } from '../types/Files';
import { localStorageKeys } from '../utils/keys/localStorageKeys';
import http from '../api/axios';

interface TranslationContextType {
  t: (query: string, entity?: EntityType) => string;
  changeLang: (lang: string, entity?: EntityType) => void;
  lang: string | null;
  documentLang: string | null;
  langSetting: LangSetting | null;
}

export const defaultLang = 'en';
const TranslationContext = createContext<TranslationContextType>({
  t: () => {
    return 'no translations';
  },
  changeLang: () => undefined,
  lang: defaultLang,
  documentLang: null,
  langSetting: null,
});

function TranslationProvider(props: any) {
  const [lang, setLang] = useState<string | null>(null);
  const [documentLang, setDocumentLang] = useState<string | null>(null);
  const [storageLang, setStorageLang] = useLocalStorage(localStorageKeys.LANGUAGE, lang);
  const [dictionary, setDictionary] = useState<any>(null);
  const [invoiceDictionary, setInvoiceDictionary] = useState<any>(null);
  const [langSetting, setLangSetting] = useState<LangSetting | null>(null);

  /**
   * Loads user companies
   */
  const fetchLangSetting = async () => {
    const response = await http.apiGet({
      url: `${apiUrl.LANG_GET_SETTING}`,
    });
    setLangSetting(response?.data);
  };

  useEffect(() => {
    fetchLangSetting();
  }, []);

  /**
   * Set app language if storage is set
   */
  useEffect(() => {
    if (lang) setStorageLang(lang);
    if (storageLang && !lang) setLang(storageLang);
    if (langSetting?.defaultLanguage && !lang && !storageLang) {
      setLang(langSetting?.defaultLanguage);
      setStorageLang(langSetting?.defaultLanguage);
    }
  }, [storageLang, langSetting, lang]);

  /**
   * When lang of app changes, import dictionary
   */
  useEffect(() => {
    if (!lang) return;
    const _dictionary = require(`../localization/${lang.toLocaleLowerCase()}.json`);
    if (_dictionary) setDictionary(_dictionary);
    setStorageLang(lang);
  }, [lang]);

  /**
   * When lang of invoice changes, import dictionary
   */
  useEffect(() => {
    if (!documentLang) return;
    const _invoiceDictionary = require(`../localization/${documentLang.toLocaleLowerCase()}.json`);
    if (_invoiceDictionary) setInvoiceDictionary(_invoiceDictionary);
  }, [documentLang]);

  /**
   * Select dictionary
   * @param query
   * @param entity - select dictionary by entity type
   */
  const t = (query: string, entity?: EntityType) => {
    if (!dictionary || (!invoiceDictionary && entity === EntityType.ACCOUNTING_DOCUMENT)) return;
    if (entity === EntityType.ACCOUNTING_DOCUMENT && invoiceDictionary)
      return getStringFromQuery(query, invoiceDictionary);
    return getStringFromQuery(query, dictionary);
  };

  /**
   * Get string from query and selected dictionary
   * @param query
   * @param current
   */

  const getStringFromQuery = (query: string, current: any) => {
    const queryArr = query && query.split('.');
    if (!queryArr) return `!${query}`;

    let result = current;
    for (const q of queryArr) {
      if (result && q in result) {
        result = result[q];
      } else {
        result = `!${query}`;
        break;
      }
    }

    return result;
  };

  /**
   * Change language
   * @param lang
   * @param isInvoice - could be selected different language for invoice
   */
  const changeLang = (lang: string, entity?: EntityType) => {
    // TODO - nastavit locale
    // if (lang === Lang.en) moment.locale('en');
    // else moment.locale('cs');
    if (entity === EntityType.ACCOUNTING_DOCUMENT) {
      setDocumentLang(lang);
    } else setLang(lang);
  };

  return (
    <TranslationContext.Provider
      value={{ t, changeLang, lang, documentLang, langSetting }}
      {...props}
    />
  );
}

const useTranslation = () => useContext<TranslationContextType>(TranslationContext);

export { TranslationProvider, useTranslation };
